/**
 * Tab Derivation Utilities
 * Functions for deriving tab definitions from filter state
 */

import { CanonicalLoad } from '@numeo/types-core'
import { LoadFiltersState, LoadSearchTab, LocationData } from '../types/filters'
import { filterLoads } from './filters'

/**
 * Format location for display in tabs
 * Handles various location data structures with defensive checks
 */
export const formatLocation = (location: LocationData | null): string => {
    if (!location || typeof location !== 'object') return 'Anywhere'

    // Check for city and stateProv (standard SearchParams structure)
    const city = location.city?.trim()
    const stateProv = location.stateProv?.trim()
    if (city && stateProv) {
        return `${city}, ${stateProv}`
    }

    // Fallback: try stateName if stateProv is missing (LocationData structure)
    const locationWithStateName = location as LocationData & { stateName?: string; stateCode?: string }
    const stateName = locationWithStateName.stateName?.trim()
    if (city && stateName) {
        return `${city}, ${stateName}`
    }

    // Fallback: try stateCode if stateProv is missing
    const stateCode = locationWithStateName.stateCode?.trim()
    if (city && stateCode) {
        return `${city}, ${stateCode}`
    }

    // If we have at least a city, show it
    if (city) {
        return city
    }

    // State-only selection (no city)
    if (stateProv) {
        return stateProv
    }

    // Zone-only selection (a DAT zone chip has no city/state — show the zone code)
    if (location.zoneCode?.trim()) {
        return location.zoneCode.trim()
    }

    return 'Anywhere'
}

/**
 * Derive tab definitions from filters
 * Tabs are dynamically generated from filter state, loads, and pagination
 */
export const deriveTabsFromFilters = (
    tabFilters: Record<string, LoadFiltersState>,
    tabLoads?: Record<string, CanonicalLoad[]>,
    hiddenLoads?: Record<string, Set<string>>
): LoadSearchTab[] => {
    return Object.keys(tabFilters).map((tabId) => {
        const filters = tabFilters[tabId]

        const origin = formatLocation(filters.origin)
        const destination = formatLocation(filters.destination)

        // Always calculate count from actual loaded data — this correctly reflects
        // all sources (including DAT_EXTENSION which arrives via postMessage), hidden
        // loads, and client-side filters. The previous approach used the API total
        // (summary.totalLoads) which was a pre-dedup sum and excluded extension loads.
        const rawLoads = tabLoads?.[tabId] || []
        let filteredLoads = filterLoads(rawLoads, filters)

        // Exclude hidden loads
        const hiddenSet = hiddenLoads?.[tabId]
        if (hiddenSet && hiddenSet.size > 0) {
            filteredLoads = filteredLoads.filter((load) => !hiddenSet.has(String(load.id)))
        }

        const sourceCounts: Record<string, number> = {}
        for (const load of filteredLoads) {
            if (load.sourceType) sourceCounts[load.sourceType] = (sourceCounts[load.sourceType] ?? 0) + 1
        }

        return {
            id: tabId,
            origin,
            destination,
            count: filteredLoads.length,
            sourceCounts,
        }
    })
}
